import React, { useCallback, useEffect, useRef, useState } from 'react'
import { LuCheck, LuListFilter, LuX } from 'react-icons/lu'

import { Organization } from '@/feature/observatory'


import { OpeningFilterPopout } from '../ExecutionBoardFilters/ExecutionBoardFilters'
import styles from '../ExecutionBoardFilters/ExecutionBoardFilters.module.css'

export const TaskOwnerFilterPopout = ({
  setFilteringTaskOwner,
  availableTaskOwners,
  currentSelectedOrgId,
  openingFilterPopout,
  onOpenFilterPopout,
}: {
  setFilteringTaskOwner: (orgId: number | null) => void
  availableTaskOwners: Organization[]
  currentSelectedOrgId?: number
  openingFilterPopout: OpeningFilterPopout | null
  onOpenFilterPopout: (popout: OpeningFilterPopout) => void
}) => {
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null)
  const filterButtonRef = useRef<HTMLDivElement>(null)
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [popoverPosition, setPopoverPosition] = useState(
    filterButtonRef.current?.getBoundingClientRect() ?? { bottom: 0, right: 0 },
  )

  const showPopover = (e: React.MouseEvent) => {
    e.stopPropagation()
    const pos = filterButtonRef.current?.getBoundingClientRect()
    if (pos) {
      setPopoverPosition(pos)
    }
    dialogRef?.current?.showPopover()
    onOpenFilterPopout(OpeningFilterPopout.TASK_OWNER)
  }
  const { bottom, right } = popoverPosition

  const handleClose = useCallback(
    (applyClose: boolean = false) => {
      if (dialogRef.current) {
        if (!applyClose && currentSelectedOrgId !== selectedOrgId) {
          setSelectedOrgId(currentSelectedOrgId ?? null)
        }
        dialogRef.current.hidePopover()
      }
    },
    [currentSelectedOrgId, selectedOrgId],
  )

  const handleSelectTaskOwner = useCallback(
    (orgId: number) => {
      if (orgId === selectedOrgId) {
        setSelectedOrgId(null)
      } else {
        setSelectedOrgId(orgId)
      }
    },
    [selectedOrgId, setSelectedOrgId],
  )

  const handleApply = useCallback(() => {
    setFilteringTaskOwner(selectedOrgId)
    handleClose(true)
  }, [selectedOrgId, setFilteringTaskOwner, handleClose])

  const handleClear = useCallback(() => {
    setSelectedOrgId(null)
    setFilteringTaskOwner(null)
  }, [setFilteringTaskOwner])

  useEffect(() => {
    if (openingFilterPopout !== OpeningFilterPopout.TASK_OWNER) {
      handleClose()
    }
  }, [openingFilterPopout])

  return (
    <div>
      <div
        ref={filterButtonRef}
        className={styles.button}
        data-testid="execution-board-button"
        onClick={showPopover}
      >
        <LuListFilter size="16" />
        Task Owner
      </div>

      <dialog
        ref={dialogRef}
        className={styles.orgPopoutContainer}
        style={{ top: bottom + 10, left: right - 80 }}
        popover="manual"
      >
        <div className={styles.popoutHeader}>
          <div className={styles.headerActions}>
            <button
              className={styles.actionButton}
              onClick={() => {
                handleClose(true)
              }}
            >
              <LuX size={16} />
            </button>
          </div>
        </div>

        <div className={styles.radioWrapper}>
          {availableTaskOwners.map((org: Organization) => (
            <label key={`option-org-${org.id}`} className={styles.basicRadio}>
              <input
                type="radio"
                value={org.id}
                checked={org.id === selectedOrgId}
                onChange={() => {}}
                onClick={() => handleSelectTaskOwner(org.id)}
              />
              {org.name}
              {org.id === selectedOrgId && <LuCheck size="24" className={styles.check} strokeWidth={1.5} />}
            </label>
          ))}
        </div>
        <div className={styles.footerActions}>
          <button className={styles.clearAllButton} onClick={handleClear}>
            Clear all
          </button>
          <button className={styles.applyButton} onClick={handleApply}>
            Apply
          </button>
        </div>
      </dialog>
    </div>
  )
}
